import React from 'react';
import { Box, Typography, Alert, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

const StyledAlert = styled(Alert)(({ }) => ({
  backgroundColor: 'rgba(211, 47, 47, 0.08)',
  color: '#d32f2f',
  padding: '10px 16px',
  borderRadius: '4px',
  boxShadow: 'none',
  marginTop: '8px',
  marginBottom: '16px',
  '& .MuiAlert-icon': {
    color: '#d32f2f',
    marginRight: '8px',
    padding: 0,
  },
  '& .MuiAlert-message': {
    padding: 0,
    fontSize: '0.875rem',
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

interface ApiErrorLabelProps {
  message?: string;
  show?: boolean;
  variant?: 'standard' | 'alert';
  testId?: string;
  sx?: Record<string, any>;
  onDismiss?: () => void;
  dismissible?: boolean;
}

const ApiErrorLabel: React.FC<ApiErrorLabelProps> = ({
  message,
  show = !!message,
  variant = 'alert',
  testId,
  sx = {},
  onDismiss,
  dismissible = true,
}) => {
  if (!show || !message) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss();
    }
  };

  if (variant === 'alert') {
    return (
      <Box data-testid={testId} sx={{ width: '100%', ...sx }}>
        <StyledAlert
          severity="error"
          icon={<ErrorOutlineIcon />}
          action={
            dismissible && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
                sx={{ ml: 1 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }
        >
          {message}
        </StyledAlert>
      </Box>
    );
  }

  return (
    <Box
      data-testid={testId}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        color: '#d32f2f',
        gap: 1,
        mt: 1,
        mb: 2,
        ...sx
      }}
    >
      <ErrorOutlineIcon fontSize="small" />
      <Typography variant="body2" sx={{ flex: 1 }}>{message}</Typography>
      {dismissible && (
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={handleDismiss}
          sx={{ p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default ApiErrorLabel;